import { useEffect, useContext, useRef } from 'react';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { ProfilePage } from './ProfilePage';
import { isElectron } from '../../api';
import { Remote } from './Remote';
import { useAuth } from '@clerk/clerk-react';
import { MinimalSteamLogin, PRE_MINIMAL_LOGIN_BOUNDS_KEY } from './MinimalSteamLogin';
import { PavilionPostLogin } from './PavilionPostLogin';

type SteamProps = {
  hasAcceptedOnboarding: boolean;
  onAcceptOnboarding: () => void;
};

export const Steam = ({ hasAcceptedOnboarding, onAcceptOnboarding }: SteamProps) => {
  const context = useContext(PlaycastContext);
  const { isSignedIn, isLoaded: clerkLoaded } = useAuth();
  /** After Clerk settles, only expand on false→true (interactive sign-in), not when already signed in at cold start. */
  const signedInBaselineRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isElectron || !window.playcast?.steamInstall) return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    html.classList.add('pavilion-steam-root');
    body.classList.add('pavilion-steam-body');
    root?.classList.add('pavilion-steam-root-el');
    return () => {
      html.classList.remove('pavilion-steam-root');
      body.classList.remove('pavilion-steam-body');
      root?.classList.remove('pavilion-steam-root-el');
    };
  }, []);

  // After sign-in: restore pre-login bounds if we shrank from a large window; otherwise grow to standard host size.
  // Guest (/guest) pavilion stays at the compact 350×530 design size.
  useEffect(() => {
    if (!isElectron || !clerkLoaded || !window.playcast?.steamInstall) return;
    if (window.playcast?.guestAccount) return;
    const wc = window.windowControls;
    if (!wc) return;

    if (signedInBaselineRef.current === null) {
      signedInBaselineRef.current = isSignedIn;
      console.log('[PlaycastWindowBounds] Steam auth baseline (no resize)', { isSignedIn, clerkLoaded });
      return;
    }

    const prev = signedInBaselineRef.current;
    signedInBaselineRef.current = isSignedIn;
    if (!isSignedIn || prev) return;

    // PavilionPostLogin is the new post-login page (compact shell). Only expand if the user
    // has explicitly proceeded to the full host (hasAcceptedOnboarding).
    if (!hasAcceptedOnboarding) {
      console.log('[PlaycastWindowBounds] Steam post-login skip (PavilionPostLogin — not yet accepted onboarding)');
      return;
    }

    console.log('[PlaycastWindowBounds] Steam post-login transition false→true');
    void (async () => {
      try {
        const raw = sessionStorage.getItem(PRE_MINIMAL_LOGIN_BOUNDS_KEY);
        if (raw) {
          sessionStorage.removeItem(PRE_MINIMAL_LOGIN_BOUNDS_KEY);
          const prevBounds = JSON.parse(raw) as { x: number; y: number; width: number; height: number };
          console.log('[PlaycastWindowBounds] Steam post-login restore session bounds', prevBounds);
          await wc.setBounds(prevBounds);
          return;
        }
        const cur = await wc.getBounds();
        const w = 1125;
        const h = 750;
        console.log('[PlaycastWindowBounds] Steam post-login getBounds', cur, 'target', { w, h });
        if (cur.width >= w - 40 && cur.height >= h - 40) {
          console.log('[PlaycastWindowBounds] Steam post-login skip (already large enough)');
          return;
        }
        const nx = Math.round(cur.x + (cur.width - w) / 2);
        const ny = Math.round(cur.y + (cur.height - h) / 2);
        const next = { x: nx, y: ny, width: w, height: h };
        console.log('[PlaycastWindowBounds] Steam post-login setBounds', next);
        await wc.setBounds(next);
        console.log('[PlaycastWindowBounds] Steam post-login getBounds after', await wc.getBounds());
      } catch (e) {
        console.warn('[Steam] post-login window bounds failed', e);
      }
    })();
  }, [isSignedIn, clerkLoaded, hasAcceptedOnboarding]);

  // Cold start signed-in users never get the false→true transition; expand once they're past onboarding.
  useEffect(() => {
    if (!isElectron || !clerkLoaded || !hasAcceptedOnboarding || !isSignedIn) return;
    if (!window.playcast?.steamInstall || window.playcast?.guestAccount) return;
    const wc = window.windowControls;
    if (!wc) return;
    void (async () => {
      try {
        if (sessionStorage.getItem(PRE_MINIMAL_LOGIN_BOUNDS_KEY)) {
          console.log('[PlaycastWindowBounds] Steam post-onboarding skip (PRE_MINIMAL key set)');
          return;
        }
        const cur = await wc.getBounds();
        const w = 1125;
        const h = 750;
        console.log('[PlaycastWindowBounds] Steam post-onboarding getBounds', cur, 'target', { w, h });
        if (cur.width >= w - 40 && cur.height >= h - 40) {
          console.log('[PlaycastWindowBounds] Steam post-onboarding skip (already large enough)');
          return;
        }
        const nx = Math.round(cur.x + (cur.width - w) / 2);
        const ny = Math.round(cur.y + (cur.height - h) / 2);
        const next = { x: nx, y: ny, width: w, height: h };
        console.log('[PlaycastWindowBounds] Steam post-onboarding setBounds', next);
        await wc.setBounds(next);
        console.log('[PlaycastWindowBounds] Steam post-onboarding getBounds after', await wc.getBounds());
      } catch (e) {
        console.warn('[Steam] post-onboarding window bounds failed', e);
      }
    })();
  }, [hasAcceptedOnboarding, isSignedIn, clerkLoaded]);

  // On load, change the edition immediately if we are logged in or in guest mode.
  useEffect(() => {
    const isGuestAccount = context.stateStores.hostMatchmakingState.guestAccount;
    const hasNonHostPassToken = !context.stateStores.authState.accessToken.startsWith('hpl') && context.stateStores.authState.accessToken;

    console.log('[Steam.tsx] Edition check:', {
      isGuestAccount,
      hasNonHostPassToken,
      accessToken: context.stateStores.authState.accessToken,
      currentEdition: context.stateStores.hostState.edition,
      steamInstall: context.stateStores.steamInstallState?.steamInstall,
    });

    if (isGuestAccount || hasNonHostPassToken) {
      console.log('[Steam.tsx] Setting edition to pavilion');
      context.targetFunctions.changeEdition('pavilion');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.stateStores.authState.accessToken, context.stateStores.hostMatchmakingState.guestAccount, context.targetFunctions]);

  // Callback to accept onboarding
  const handleAcceptOnboarding = () => {
    onAcceptOnboarding();
    context.targetFunctions.changeEdition('pavilion');
  };

  // useEffect(() => {
  //   const edition = context.stateStores.hostState.edition;
  //   if (edition === 'pavilion') {
  //     setHasAcceptedOnboarding(true);
  //   }
  // }, [context.stateStores.hostState.edition]);

  const versionState = context.stateStores.versionState;
  const hostStatus = context.stateStores.hostState.status;
  const hostAllowsUpdateUi = hostStatus !== 'streaming' && hostStatus !== 'starting';
  const updateIgnored = (() => {
    try {
      const raw = localStorage.getItem('ignoredUpdates');
      const list: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) && list.includes(versionState.updateVersion);
    } catch { return false; }
  })();
  const needsUpdate = Boolean(hostAllowsUpdateUi && versionState.hasUpdate && !updateIgnored);

  if (!isElectron) return <Remote />;

  if (!isSignedIn || needsUpdate) {
    return <MinimalSteamLogin />;
  }

  if (!hasAcceptedOnboarding) {
    return <PavilionPostLogin />;
  }

  return (
    <div className='flex w-full flex-col overflow-auto'>
      <ProfilePage />
    </div>
  );
};
